import type { Indicador, IndicadorNaTela } from '../../../types/chamado'

/**
 * Como cada indicador do ANEXO VIII se escreve na tela.
 *
 * **A escala não é a mesma nos seis, e confundi-las é errar o número por cem vezes.** O ICA é uma
 * razão de 0 a 1 — o TR escreve as faixas dele assim ("0,88 a 0,94") — e o IAP e o IAE são
 * percentuais de 0 a 100 ("De 80% a 89%"). Exibir o ICA como "93%" ao lado de uma faixa escrita
 * "0,88 a 0,94" deixaria o número impossível de conferir contra o contrato, que é a única coisa que
 * esta tela existe para permitir.
 *
 * O `Record` sobre `Indicador` é o que cobra a escala de todo indicador novo: sem a entrada, o
 * build para aqui em vez de a tela escolher um formato por omissão.
 */
export type EscalaDoIndicador = 'RAZAO' | 'PERCENTUAL' | 'PONTOS'

const ESCALA: Record<Indicador, EscalaDoIndicador> = {
  ICA: 'RAZAO',
  IAP: 'PERCENTUAL',
  IAE: 'PERCENTUAL',
  // O IAD não é razão nem percentual: é uma CONTAGEM de pontos, e o TR escreve as faixas dele
  // assim ("De 11 a 20 Pontos"). Formatá-lo como razão imprimiria "17,00" ao lado de uma faixa
  // escrita em inteiros — o número deixaria de ser conferível contra a tabela do contrato.
  IAD: 'PONTOS',
  TIS: 'PERCENTUAL',
  // O IIE não é apurado por este módulo e nunca chega a ser formatado. A escala está declarada
  // mesmo assim, na escala em que o TR escreve as faixas dele, para o dia em que ele passar a ter
  // número não haver uma decisão de formatação tomada às pressas.
  IIE: 'PERCENTUAL',
}

const DUAS_CASAS = new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
const ATE_DUAS_CASAS = new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 2 })
const INTEIRO = new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 0 })
const ATE_QUATRO_CASAS = new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 4 })

/**
 * O valor apurado, na escala e na pontuação do contrato.
 *
 * Duas casas decimais, que é como o TRP assinado imprime os dois formatos: "ICA = 1 – (3/45) =
 * 93,33%" na razão de quatro casas do domínio vira 0,93 na escala de duas casas da tabela de
 * faixas, e "IAP = 25/36 = 69,44%" fica como está. A vírgula é decimal porque o documento é em
 * pt-BR e alguém vai comparar os dois lado a lado.
 *
 * @param codigo qual dos seis indicadores
 * @param valor o valor apurado, ou nulo quando não houve apuração
 * @returns o valor escrito, ou nulo quando não há valor — e nulo aqui nunca é zero
 */
export function valorPorExtenso(codigo: Indicador, valor: number | null): string | null {
  if (valor == null) {
    return null
  }
  if (ESCALA[codigo] === 'PONTOS') {
    return INTEIRO.format(valor) + (valor === 1 ? ' ponto' : ' pontos')
  }
  return ESCALA[codigo] === 'PERCENTUAL' ? `${DUAS_CASAS.format(valor)}%` : DUAS_CASAS.format(valor)
}

/**
 * O valor apurado com toda a precisão que o domínio guarda.
 *
 * **Existe por causa de uma armadilha de exibição que decide dinheiro.** O valor de tela sai com
 * duas casas, que é como o contrato escreve as faixas; mas o domínio apura com quatro. Um ICA de
 * 0,9451 e o seu enquadramento 0,95 saem os DOIS como "0,95" em duas casas — e caem em faixas
 * diferentes (2% e 0%). Comparar os textos formatados para decidir se houve arredondamento
 * esconderia justamente o caso em que ele custou dinheiro.
 *
 * Usado onde o número precisa ser conferido contra o contrato, e não só lido: o termo de
 * recebimento.
 *
 * @param codigo qual dos seis indicadores
 * @param valor o valor apurado, ou nulo
 * @returns o valor escrito com até quatro casas, ou nulo
 */
export function valorPorExtensoPreciso(codigo: Indicador, valor: number | null): string | null {
  if (valor == null) {
    return null
  }
  if (ESCALA[codigo] === 'PONTOS') {
    return INTEIRO.format(valor) + (valor === 1 ? ' ponto' : ' pontos')
  }
  const escrito = ATE_QUATRO_CASAS.format(valor)
  return ESCALA[codigo] === 'PERCENTUAL' ? `${escrito}%` : escrito
}

/**
 * A fração que produziu o valor, como o TRP a imprime.
 *
 * O numerador do IAE não é uma contagem — ele soma os aceites integrais com um terço dos parciais —,
 * e por isso pode vir quebrado. Sai com até duas casas, sem forçar as duas: "15" é mais legível que
 * "15,00" e "15,33" continua exato.
 *
 * @param indicador o indicador apurado
 * @returns a fração escrita, ou nulo quando o indicador não foi apurado
 */
export function fracaoPorExtenso(indicador: IndicadorNaTela): string | null {
  if (indicador.numerador == null || indicador.denominador == null) {
    return null
  }
  return `${ATE_DUAS_CASAS.format(indicador.numerador)} de ${ATE_DUAS_CASAS.format(indicador.denominador)}`
}

/**
 * O endereço da lista filtrada por um chamado do numerador.
 *
 * A composição traz o número do e-Niterói, e não o identificador interno, então o caminho até o
 * chamado passa pela busca da listagem — que procura exatamente nesse campo. É um endereço só,
 * compartilhável, e é o que permite ao fiscal sair do indicador e chegar no registro que o formou.
 *
 * @param idExterno número do chamado no e-Niterói
 * @returns o endereço da listagem já filtrada
 */
export function enderecoDoChamado(idExterno: string): string {
  return `/chamados?q=${encodeURIComponent(idExterno)}`
}

/**
 * O endereço da lista de todos os chamados de uma competência.
 *
 * @param competencia mês de referência no formato `AAAA-MM`
 * @returns o endereço da listagem do mês
 */
export function enderecoDaCompetencia(competencia: string): string {
  return `/chamados?competencia=${encodeURIComponent(competencia)}`
}

/**
 * O endereço da FILA: os chamados de um mês que ainda esperam julgamento do fiscal.
 *
 * É o recorte que o fechamento cobra. `fechar()` recusa a competência enquanto sobrar um chamado sem
 * julgamento, e agosto/2026 tem 107 deles — este link é o que transforma essa recusa numa lista de
 * trabalho em vez de uma parede.
 *
 * @param competencia mês de referência no formato `AAAA-MM`
 * @returns o endereço da listagem do mês, filtrada pelos que faltam julgar
 */
export function enderecoDaFilaDeJulgamento(competencia: string): string {
  return `${enderecoDaCompetencia(competencia)}&julgado=false`
}

/**
 * Este indicador ficou sem número nesta competência?
 *
 * A pergunta parece trivial e não é: o discriminante é `apurado`, **nunca `valor != null`**. O TIS e
 * o IIE são apurados com valor nulo, porque se apuram somando e consolidando em vez de medir uma
 * nota. Escrever a checagem à mão em cada tela produziria, mais cedo ou mais tarde, uma que trata um
 * desconto real como ausência de apuração.
 *
 * @param fechamento a apuração do mês
 * @param codigo a sigla do indicador
 * @returns true quando ele não pôde ser apurado
 */
export function indicadorSemNumero(
  fechamento: { indicadores: { codigo: Indicador; apurado: boolean }[] },
  codigo: Indicador,
): boolean {
  return fechamento.indicadores.some((i) => i.codigo === codigo && !i.apurado)
}
